import {
  AcceptConditions,
  AuthForm,
  CheckBox,
  CheckBoxContent,
  CheckField,
  CountryCode,
  ErrorMessage,
  FieldWrapper,
  Input,
  InputIcon,
  RegisterFormFooter,
  Signup,
  SignUpButton,
  StyledPhoneInput,
  SubTitle,
  Title,
  UnderLineLink,
} from '@Components/Auth/Auth.styled';
import { Spinner } from '@Components/common/Spinner';
import { _Register } from '@Services/Authentication';
import { AxiosError } from 'axios';
import { CountryCode as CountryCodeType } from 'libphonenumber-js/types';
import useTranslation from 'next-translate/useTranslation';
import Image from 'next/image';
import Hide from 'public/hide.svg';
import Logo from 'public/logo.svg';
import Show from 'public/show.svg';
import { useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { getCountryCallingCode, isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { RegisterFormValues } from 'types/User';

type Props = {
  setIsFormSuccess: React.Dispatch<React.SetStateAction<boolean>>;
};

interface RegisterForm extends RegisterFormValues {
  confirmPassword: string;
  confirmTerms: boolean;
}

export default function SignupForm({ setIsFormSuccess }: Props) {
  const { t: tAuth } = useTranslation('Auth');
  const [showPassword, setShowPassword] = useState<Boolean>(false);
  const [submitHasError, setSubmitHasError] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [country, setCountry] = useState<CountryCodeType | undefined>('EG');
  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm<RegisterForm>();

  const handleOnSubmit: SubmitHandler<RegisterForm> = ({ confirmPassword, confirmTerms, ...data }) => {
    setIsLoading(true);
    setSubmitHasError('');
    _Register({
      ...data,
      email: data.email.toLowerCase(),
    })
      .then(() => {
        setIsFormSuccess(true);
      })
      .catch((err: AxiosError) => {
        const key = Object.keys(err.response?.data)[0];
        setSubmitHasError(err.response?.data?.[key]?.[0] || tAuth('something-went-wrong'));
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <AuthForm onSubmit={handleSubmit(handleOnSubmit)}>
      <Image src={Logo} width={235.61} height={33.02} alt="auth-logo" />
      <Title>{tAuth('signup_title')}</Title>
      <SubTitle>{tAuth('signup_subtitle')}</SubTitle>
      <FieldWrapper>
        <Input
          type="text"
          disabled={isLoading}
          placeholder={tAuth('full_name')}
          {...register('full_name', { required: true, maxLength: 20 })}
        />
        <ErrorMessage>{errors.full_name?.type === 'required' && tAuth('name_error')}</ErrorMessage>
      </FieldWrapper>
      <FieldWrapper>
        <CountryCode>+{country ? getCountryCallingCode(country) : ''}</CountryCode>
        <Controller
          name="mobile"
          control={control}
          rules={{
            required: true,
            validate: (value) => isValidPhoneNumber(value.toString()),
          }}
          render={({ field: { onChange, value } }) => (
            <StyledPhoneInput
              value={value?.toString()}
              onChange={onChange}
              placeholder={tAuth('phone')}
              defaultCountry={country}
              onCountryChange={(country?: CountryCodeType) => setCountry(country)}
              disabled={isLoading}
              id="PHONE"
            />
          )}
        />
        <ErrorMessage>
          {Boolean(errors.mobile) &&
            (errors.mobile?.type === 'validate' ? tAuth('phone_error_validate') : tAuth('phone_error'))}
        </ErrorMessage>
      </FieldWrapper>
      <FieldWrapper>
        <Input
          type="email"
          disabled={isLoading}
          placeholder={tAuth('email')}
          {...register('email', { required: true })}
        />
        <ErrorMessage>{errors.email?.type === 'required' && tAuth('email_error')}</ErrorMessage>
      </FieldWrapper>
      <FieldWrapper>
        <Input
          type={showPassword ? 'text' : 'password'}
          placeholder={tAuth('create_password')}
          disabled={isLoading}
          hasIcon
          {...register('password', { required: true, minLength: 8 })}
        />
        <InputIcon>
          <Image
            src={showPassword ? Hide : Show}
            width={24}
            height={14.64}
            alt="password_icon"
            onClick={() => setShowPassword((prev) => !prev)}
          />
        </InputIcon>
        <ErrorMessage>
          {Boolean(errors.password) &&
            (errors.password?.type === 'required' ? tAuth('password_error') : tAuth('password_minlength'))}
        </ErrorMessage>
      </FieldWrapper>
      {Boolean(watch('password')?.length) && (
        <FieldWrapper>
          <Input
            type={showPassword ? 'text' : 'password'}
            hasIcon
            disabled={isLoading}
            placeholder={tAuth('confirm_password')}
            {...register('confirmPassword', { required: true, validate: (val) => val === watch('password') })}
          />
          <InputIcon>
            <Image
              src={showPassword ? Hide : Show}
              width={24}
              height={14.64}
              alt="password_icon"
              onClick={() => setShowPassword((prev) => !prev)}
            />
          </InputIcon>
          <ErrorMessage>
            {Boolean(errors.confirmPassword) &&
              (errors.confirmPassword?.type === 'required' ? tAuth('password_error') : tAuth('password_match'))}
          </ErrorMessage>
        </FieldWrapper>
      )}
      <AcceptConditions>
        <CheckField htmlFor="confirmTerms">
          <input type="checkbox" id="confirmTerms" {...register('confirmTerms', { required: true })} />
          <CheckBox></CheckBox>
          <CheckBoxContent>
            {tAuth('condition_message')}
            <UnderLineLink href="/">{tAuth('conditions')}</UnderLineLink>
            &nbsp; &amp; &nbsp;
            <UnderLineLink href="/">{tAuth('privacy')}</UnderLineLink>
          </CheckBoxContent>
        </CheckField>
        <ErrorMessage>{errors.confirmTerms?.type === 'required' && tAuth('must-terms-conditions')}</ErrorMessage>
      </AcceptConditions>
      <SignUpButton type="submit" variant="filled-red" disabled={isLoading}>
        {isLoading ? <Spinner /> : tAuth('sign_up')}
      </SignUpButton>
      {submitHasError && <ErrorMessage>{submitHasError}</ErrorMessage>}
      <RegisterFormFooter>
        <p>{tAuth('no_account')}</p>
        &nbsp;
        <Signup href="/auth/login">{tAuth('sign_in')}</Signup>
      </RegisterFormFooter>
    </AuthForm>
  );
}
